import { isSceneObjectToken } from '../combat/ObjectInteractionTargeting';

const finiteNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const resolveTokenGridCell = (token, gridSize = 50) => {
  const nestedX = finiteNumber(token?.grid?.x);
  const nestedY = finiteNumber(token?.grid?.y);
  if (nestedX !== null && nestedY !== null) {
    return { x: Math.round(nestedX), y: Math.round(nestedY) };
  }

  const legacyX = finiteNumber(token?.gridX);
  const legacyY = finiteNumber(token?.gridY);
  if (legacyX !== null && legacyY !== null) {
    return { x: Math.round(legacyX), y: Math.round(legacyY) };
  }

  const pixelsPerCell = finiteNumber(gridSize);
  const pixelX = finiteNumber(token?.x);
  const pixelY = finiteNumber(token?.y);
  if (pixelsPerCell && pixelsPerCell > 0 && pixelX !== null && pixelY !== null) {
    return {
      x: Math.round(pixelX / pixelsPerCell),
      y: Math.round(pixelY / pixelsPerCell),
    };
  }

  return null;
};

/**
 * Selects a full-size primary occupant for each grid square and exposes every
 * other occupant as a compact, clickable badge. This is presentation only:
 * token coordinates and identities are never copied or changed.
 * date_of_change: 2026-07-17
 */
export const buildCoLocatedTokenLayout = (tokens, gridSize = 50, selectedTokenId = null) => {
  const source = Array.isArray(tokens) ? tokens.filter(Boolean) : [];
  const groups = new Map();

  source.forEach((token, sourceIndex) => {
    const cell = resolveTokenGridCell(token, gridSize);
    const layer = String(token?.layer || 'token').toLowerCase();
    const key = cell ? `${layer}:${cell.x}:${cell.y}` : `unplaced:${sourceIndex}`;
    if (!groups.has(key)) groups.set(key, { cell, members: [] });
    groups.get(key).members.push({ token, sourceIndex });
  });

  const orderedTokens = [];
  const badges = [];
  const foregroundTokenIds = [];

  groups.forEach(({ cell, members }) => {
    if (!cell || members.length === 1) {
      orderedTokens.push(...members.map(({ token }) => token));
      return;
    }

    // A creature remains readable at full size when it shares a square with a
    // scene object. The latest non-object retains the legacy topmost behavior.
    // Initiative/sidebar/badge selection must expose the selected occupant as
    // the full interactive token. Otherwise a valid selection remains buried
    // under the previous primary and cannot be moved out of a crowded square.
    // Preserve the creature-over-object fallback when nothing is selected.
    // date_of_change: 2026-08-25
    const selectedMember = selectedTokenId
      ? members.find(({ token }) => token.id === selectedTokenId)
      : null;
    const primaryMember = selectedMember
      || [...members].reverse().find(({ token }) => !isSceneObjectToken(token))
      || members[members.length - 1];
    const secondaryMembers = members.filter(({ token }) => token.id !== primaryMember.token.id);

    // A selected occupant must be painted after the compact badges. Otherwise
    // a crowded badge grid consumes every pointer gesture over the cell: the
    // sidebar can select the actor, but the user still cannot drag it away.
    // Keeping this as presentation metadata avoids changing scene order or
    // token state. date_of_change: 2026-08-25
    if (selectedMember) foregroundTokenIds.push(primaryMember.token.id);

    // Keep the base token pass secondary-first and primary-last. The renderer
    // moves a selected primary into a foreground pass after the compact badges.
    orderedTokens.push(...secondaryMembers.map(({ token }) => token), primaryMember.token);
    secondaryMembers.forEach(({ token }, slotIndex) => {
      badges.push({
        token,
        cell,
        slotIndex,
        slotCount: secondaryMembers.length,
        primaryTokenId: primaryMember.token.id,
      });
    });
  });

  return { orderedTokens, badges, foregroundTokenIds };
};

export default buildCoLocatedTokenLayout;
